import { acquireToken } from "@/auth/msal";
import {
  ApiError,
  ConflictError,
  ForbiddenError,
  UnauthorizedError,
  type AdminCredentialsResponse,
  type CreatePlaygroundRequest,
  type CreatePlaygroundResponse,
  type DnsZone,
  type Me,
  type Playground,
  type PlaygroundStatusResponse,
  type SeqCredentialsResponse,
  type SharedoVersionsResponse,
} from "./types";
import * as mock from "./mock";

const useMock = import.meta.env.VITE_USE_MOCK_API === "true";

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let token: string | null = null;
  try {
    token = await acquireToken();
  } catch {
    // No token; backend will reject with 401 and we surface that below.
  }

  // Build headers via the Headers class so case-different duplicate keys
  // (e.g. "content-type" + "Content-Type" from an over-helpful caller)
  // collapse to one value. Plain object spread does NOT dedupe — both
  // keys end up in the literal and fetch comma-joins them, which makes
  // Express's express.json() silently skip parsing because
  // `application/json, application/json` doesn't match the type-is filter.
  // (That cost us an hour of network-tab diagnostics on the upgrade flow.)
  const headers = new Headers();
  headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.headers) {
    new Headers(init.headers as HeadersInit).forEach((value, key) => {
      headers.set(key, value);
    });
  }
  const res = await fetch(path, { ...init, headers });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get("content-type") ?? "";
  const body: unknown = contentType.includes("application/json")
    ? await res.json().catch(() => undefined)
    : await res.text().catch(() => undefined);

  if (res.ok) return body as T;

  // Backend convention: `{ error: <short>, detail?: <full message> }`. Surface
  // both as `<short>: <detail>` so users see the actionable Azure error text
  // (permission denied / container not found / etc.) instead of a generic line.
  const errorField =
    typeof body === "object" && body && "error" in body && typeof (body as { error: unknown }).error === "string"
      ? (body as { error: string }).error
      : null;
  const detailField =
    typeof body === "object" && body && "detail" in body && typeof (body as { detail: unknown }).detail === "string"
      ? (body as { detail: string }).detail
      : null;
  const message =
    (errorField && detailField
      ? `${errorField}: ${detailField}`
      : errorField ?? detailField) ?? res.statusText ?? "Request failed";

  if (res.status === 401) throw new UnauthorizedError(message, body);
  if (res.status === 403) throw new ForbiddenError(message, body);
  if (res.status === 409) throw new ConflictError(message, body);
  throw new ApiError(res.status, message, body);
}

// Public API ---------------------------------------------------------------

export const api = {
  async me(): Promise<Me> {
    if (useMock) return mock.me();
    // Backend's /api/me returns { oid, upn, roles } — doesn't yet compute
    // runningJobs (the JobTracker doesn't have a per-user query). Default
    // to [] so downstream `runningJobs.map(...)` etc. doesn't crash.
    const raw = await request<Partial<Me>>("/api/me");
    return {
      oid: raw.oid ?? "",
      upn: raw.upn ?? "",
      roles: raw.roles ?? [],
      runningJobs: raw.runningJobs ?? [],
    };
  },

  async listPlaygrounds(): Promise<Playground[]> {
    if (useMock) return mock.listPlaygrounds();
    // Backend wraps in { playgrounds: [...] }; mock returns raw array.
    // Accept either + normalise each row to fill the missing fqdn /
    // top-level step/totalSteps fields the UI expects.
    const raw = await request<{ playgrounds: BackendPlayground[] } | BackendPlayground[]>(
      "/api/playgrounds",
    );
    const rows = Array.isArray(raw) ? raw : raw.playgrounds ?? [];
    return rows.map(normalizePlayground);
  },

  async getPlayground(customer: string): Promise<Playground> {
    if (useMock) return mock.getPlayground(customer);
    const raw = await request<BackendPlayground>(
      `/api/playgrounds/${encodeURIComponent(customer)}`,
    );
    return normalizePlayground(raw);
  },

  async getStatus(customer: string, since?: number): Promise<PlaygroundStatusResponse> {
    if (useMock) return mock.getStatus(customer, since);
    const q = since != null ? `?since=${since}` : "";
    const raw = await request<BackendStatus>(
      `/api/playgrounds/${encodeURIComponent(customer)}/status${q}`,
    );
    return normalizeStatus(raw);
  },

  async getFullLog(customer: string): Promise<string> {
    if (useMock) return mock.getFullLog(customer);
    return request<string>(`/api/playgrounds/${encodeURIComponent(customer)}/log`);
  },

  /**
   * Trigger a browser file download of the full append-log for the
   * customer's last job. Implementation: fetch with Authorization header
   * (so the Entra middleware accepts it), drop the body into a Blob,
   * synthesise an <a download> click. Native `window.open(url)` can't
   * carry the bearer token so we have to do this manually.
   *
   * Filename comes from the server's Content-Disposition; if missing,
   * fall back to `{customer}-job.log`.
   */
  async downloadFullLog(customer: string): Promise<void> {
    if (useMock) return;
    const token = await acquireToken();
    const resp = await fetch(
      `/api/playgrounds/${encodeURIComponent(customer)}/log/download`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`Download failed (${resp.status}): ${text.slice(0, 200)}`);
    }
    const disp = resp.headers.get("content-disposition") ?? "";
    const m = /filename="?([^";]+)"?/.exec(disp);
    const filename = m?.[1] ?? `${customer}-job.log`;
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  async create(req: CreatePlaygroundRequest): Promise<CreatePlaygroundResponse> {
    if (useMock) return mock.create(req);
    return request<CreatePlaygroundResponse>("/api/playgrounds", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },

  async destroy(customer: string): Promise<void> {
    if (useMock) return mock.destroy(customer);
    return request<void>(`/api/playgrounds/${encodeURIComponent(customer)}`, {
      method: "DELETE",
    });
  },

  /**
   * Wipe the current job's log blob + clear log-bearing fields on the
   * ownership blob. Used by the "Clear logs" button on the detail
   * page — useful when retrying a failed deploy/destroy and the
   * previous attempt's noise is getting in the way.
   *
   * 409 if a job is in flight; 403 if non-owner/non-admin.
   */
  async clearLogs(customer: string): Promise<void> {
    if (useMock) return;
    return request<void>(`/api/playgrounds/${encodeURIComponent(customer)}/log`, {
      method: "DELETE",
    });
  },

  /**
   * Re-fire the deploy job for a playground that ended in `failed`
   * status. Server reuses the original dnsZone/ring/sharedoVersion/
   * eeVersion — retry doesn't expose them as overrides (change params
   * = delete + recreate). 202 on success; 409 if status isn't
   * `failed`; 403 if non-owner/non-admin.
   */
  async retry(customer: string): Promise<void> {
    if (useMock) return;
    return request<void>(`/api/playgrounds/${encodeURIComponent(customer)}/retry`, {
      method: "POST",
    });
  },

  /**
   * Upgrade an existing playground to a new sharedoVersion / eeVersion,
   * or refresh it on the same version. Backend runs the orchestrator
   * in mode="upgrade" — preserves customer customisations (repos,
   * EE config). Compatibility rules enforced server-side via
   * src/lib/versionCompatibility.ts.
   *
   * Body shape:
   *   { version?: string, eeVersion?: string, refresh?: boolean }
   *
   *   - refresh:true (or both fields absent) — re-deploy same version
   *   - version (+ optional eeVersion) — upgrade to the new release
   */
  async upgrade(
    customer: string,
    payload: { version?: string; eeVersion?: string; refresh?: boolean; ring?: string },
  ): Promise<{ jobId: string; fromVersion: string; toVersion: string }> {
    if (useMock) {
      return { jobId: "mock-upgrade", fromVersion: "x", toVersion: payload.version ?? "x" };
    }
    // Note: do NOT add a `Content-Type` header here. The request() helper
    // already sets `content-type: application/json` (lowercase). Adding
    // `Content-Type` (PascalCase) here makes BOTH keys exist in the
    // merged object literal — fetch serialises that as a comma-joined
    // value `application/json, application/json`, which Express's
    // type-is matcher rejects, express.json() silently skips parsing,
    // and req.body arrives empty. That bug took an hour of network-tab
    // diagnostics to find — leave Content-Type to the helper.
    return request(`/api/playgrounds/${encodeURIComponent(customer)}/upgrade`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /**
   * Returns the current { ring, sharedoVersion, eeVersion } for the
   * customer — used by the upgrade dialog to render "Current: …"
   * without a separate status fetch. The CANDIDATE list comes from
   * listSharedoVersions(ring); the dialog filters client-side using
   * the same compatibility rules the server enforces (release → release,
   * playground → same playground only).
   */
  async getUpgradeTargets(customer: string): Promise<{
    current: { ring: string; sharedoVersion: string; eeVersion: string };
  }> {
    if (useMock) {
      return { current: { ring: "staging", sharedoVersion: "7.11.4", eeVersion: "pinned" } };
    }
    return request(
      `/api/playgrounds/${encodeURIComponent(customer)}/upgrade-targets`,
    );
  },

  /**
   * Fetch the per-customer Seq URL + admin credentials. Called lazily on
   * "Reveal" so the password never rides along with the regular status
   * poll. Returns ApiError(404) with body
   * `{ error: "seq-credentials-not-provisioned" }` for customers deployed
   * before the per-customer-Seq feature landed.
   */
  async getSeqCredentials(customer: string): Promise<SeqCredentialsResponse> {
    if (useMock) {
      return {
        url: `https://${customer}-seq.example.com`,
        username: "admin",
        password: "mock-password-do-not-use",
      };
    }
    return request<SeqCredentialsResponse>(
      `/api/playgrounds/${encodeURIComponent(customer)}/seq-credentials`,
    );
  },

  /**
   * Fetch the per-customer Operate admin login URL + credentials. Same
   * lazy-on-reveal pattern as getSeqCredentials so the plain-text password
   * doesn't ride along with every status poll. Returns ApiError(404) with
   * body `{ error: "admin-credentials-not-provisioned" }` for customers
   * deployed without the `seedAdminUser` flag — the panel renders an empty
   * state in that case rather than treating it as an error.
   */
  async getAdminCredentials(customer: string): Promise<AdminCredentialsResponse> {
    if (useMock) {
      return {
        url: `https://${customer}.example.com/`,
        username: "admin",
        password: "mock-password-do-not-use",
      };
    }
    return request<AdminCredentialsResponse>(
      `/api/playgrounds/${encodeURIComponent(customer)}/admin-credentials`,
    );
  },

  async listDnsZones(): Promise<DnsZone[]> {
    if (useMock) return mock.listDnsZones();
    const raw = await request<{ zones: string[] } | DnsZone[]>("/api/dns-zones");
    // Backend currently returns { zones: string[] }; normalise to DnsZone[].
    if (Array.isArray(raw)) return raw;
    return raw.zones.map((z, i) => ({ zone: z, default: i === 0 }));
  },

  async listSharedoVersions(
    ring: "staging" | "playground",
  ): Promise<SharedoVersionsResponse> {
    if (useMock) return mock.listSharedoVersions(ring);
    return request<SharedoVersionsResponse>(
      `/api/sharedo-versions?ring=${encodeURIComponent(ring)}`,
    );
  },
};

// Backend response shapes ---------------------------------------------------
//
// What `routes/playgrounds.ts` actually returns over the wire — distinct
// from the frontend's view-model shapes. Adapters below normalise: flatten
// the nested `live.step` into top-level step/totalSteps, fill in the
// derived `fqdn`, etc.

interface BackendPlayground {
  customer: string;
  dnsZone: string;
  fqdn?: string;             // not currently returned by backend — derived
  status: Playground["status"];
  createdBy: { oid: string; upn: string };
  createdAt: string;
  lastJob?: Playground["lastJob"];
  sharedoVersion?: string;
  eeVersion?: string;
  ring?: string;
  /** Live job snapshot — only present when a deploy/destroy is in flight. */
  live?: {
    jobId: string;
    status: "running" | "succeeded" | "failed";
    step?: { current: number; total: number };
    postPhase: boolean;
    recentLog: string[];
  };
}

interface BackendStatus {
  status: Playground["status"];
  jobId?: string;
  kind?: "deploy" | "destroy";
  step?: { current: number; total: number };
  postPhase?: boolean;
  recentLog?: string[];
  // Server-side log-tail cursor (byte offset into the append blob).
  // PlaygroundDetail.vue uses this both as the gate for whether to push
  // `recentLog` onto the page and as the `?since=` value on the next
  // poll. Without it the gate becomes `0 > 0` (never true) and the
  // since param stays at 0 forever — the symptom that surfaced as
  // "blank logs" several times.
  logCursor?: number;
  // Steps that have already finished, used by the step-progress visual.
  completedSteps?: number[];
  // Optional short summary of why a deploy failed, surfaced as a banner.
  failureSummary?: string;
}

function normalizePlayground(raw: BackendPlayground): Playground {
  // Backend doesn't currently emit fqdn; derive from customer + dnsZone.
  const fqdn = raw.fqdn ?? `${raw.customer}.${raw.dnsZone}`;
  // Live job's step is { current, total }; flatten to the top-level
  // step / totalSteps fields the dashboard + detail page bind to.
  const liveStep = raw.live?.step;
  return {
    customer: raw.customer,
    dnsZone: raw.dnsZone,
    fqdn,
    status: raw.status,
    createdBy: raw.createdBy,
    createdAt: raw.createdAt,
    lastJob: raw.lastJob,
    sharedoVersion: raw.sharedoVersion,
    eeVersion: raw.eeVersion,
    ring: raw.ring,
    step: liveStep?.current,
    totalSteps: liveStep?.total,
  };
}

function normalizeStatus(raw: BackendStatus): PlaygroundStatusResponse {
  // Backend's step is { current, total }; frontend's type splits them.
  return {
    status: raw.status,
    jobId: raw.jobId,
    step: raw.step?.current,
    totalSteps: raw.step?.total,
    // completedSteps + failureSummary + logCursor ARE populated by the
    // backend (since commit fd94d90 — server reads ?since=, returns
    // append-blob deltas with the new cursor). Pass them through so
    // the page's append gate (s.logCursor > logCursor.value) actually
    // evaluates true and the log lines render. Dropping these here is
    // what caused the "blank logs" regression — see subagent
    // investigation 2026-06-03.
    recentLog: raw.recentLog ?? [],
    logCursor: raw.logCursor,
    completedSteps: raw.completedSteps,
    failureSummary: raw.failureSummary,
    postPhase: raw.postPhase,
  };
}
